import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { type BuilderContent, isEditing } from "@builder.io/sdk-react";

import {
  areAllLevelsComplete,
  areAllModulesComplete,
  getNextIncompleteModuleID,
} from "~/hooks/learning-center/helpers";
import {
  type RawModuleEntry,
  type TransformedModule,
  createModule,
} from "~/hooks/learning-center/transformers";
import type { UpdatedDataResponse } from "~/hooks/learning-center/types";
import useAwardLevelPoints from "~/hooks/learning-center/use-award-level-points";
import {
  useFetchModule,
  useFetchModulePageUrl,
  useFetchParentGoal,
  useFetchParentLevel,
} from "~/hooks/learning-center/use-fetch-lms";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";
import { useRootLayoutData } from "~/root";

import {
  type CtaType,
  type QuizStatus,
  ctaTypes,
  quizStatuses,
} from "./constants";

/** Builder reference whose target id is at `.value.data.id` or `.data.id`. */
type LcBuilderRef = {
  value?: { data?: { id?: string } };
  data?: { id?: string };
};
/** The `data` fields of a `learning-center-level` Builder entry. */
interface LevelFields {
  id?: string;
  modules?: { module: LcBuilderRef }[];
  loyaltyActionId?: string | null;
}
/** The `data` fields of a `learning-center-goal` Builder entry. */
interface GoalFields {
  id?: string;
  levels?: { level: LcBuilderRef }[];
  dashboard?: { value?: { data?: { url?: string } } };
}

/** A lesson row from the transformed module. */
export type ModuleLesson = TransformedModule["lessons"][number];

export interface NextModuleTarget {
  id: string | null;
  url: string;
}

export interface UseLearningCenterModuleReturn {
  module: TransformedModule | null;
  requestInProgress: boolean;
  componentIsLoading: boolean;
  isError: boolean;
  /** Force a refetch of the module entry (the quiz is inlined; SFCC-11738). */
  refetchModule: () => void;
  activeAccordionItem: string | null;
  setSelectedAccordionItem: (value: string | null) => void;
  quizStatus: QuizStatus;
  setQuizStatus: (status: QuizStatus) => void;
  ctaType: CtaType | null;
  nextModuleData: NextModuleTarget;
  isGuest: boolean;
  isLevelComplete: boolean;
  submitLesson: (
    lesson: ModuleLesson,
    nextLesson: ModuleLesson | undefined,
    opts?: { markOnly?: boolean },
  ) => Promise<void>;
  submitQuiz: (data: Record<string, string>) => Promise<void>;
  goToNextModule: (target: NextModuleTarget) => Promise<boolean>;
  /** Whether the next-step CTA has a target (gates its disabled state). */
  isNextReady: boolean;
  setActiveLesson: (lessonUrl: string) => void;
}

/**
 * LMS module player state machine — port of composable
 * `useLearningCenterModule.js` (SFCC-11505), adapted to RR7 + the Workers LMS
 * data layer.
 *
 * Deltas vs composable:
 *  - `useNavigate` (RR7) replaces `useHistory().push`.
 *  - AnnexCloud enrollment / available-points / award are consolidated into ONE
 *    server resource action (`useAwardLevelPoints`) — the client only forwards
 *    the level's `loyaltyActionId`; the hook no longer runs the three client
 *    Annex queries or the profile write.
 *  - Builder content shapes come from the typed fetch hooks (`use-fetch-lms`):
 *    the parent-level/goal *entries* carry `{ id (contentID), data (fields) }`.
 *  - `useLearningCenterGuest` is dropped (guest persistence is owned by
 *    `useLearningCenter.updateData`, mirrored in the mutation layer).
 */
export default function useLearningCenterModule(
  builderModule: { id?: string } | undefined,
): UseLearningCenterModuleReturn {
  const navigate = useNavigate();
  const { isRegistered } = useRootLayoutData();
  // Treat the Builder visual editor / preview as non-guest so the 2-lesson cap,
  // "Sign In to View More" gating, and visited badges don't fire while authors
  // edit content (mirrors composable `routes.jsx:62-64` `isEditing()` bypass +
  // `goal-questionnaire` `!isRegistered && !isEditing()`).
  const isGuest = !isRegistered && !isEditing();

  const {
    data: moduleEntry,
    isLoading: isModuleLoading,
    error: moduleError,
    reload: refetchModule,
  } = useFetchModule(builderModule?.id);
  // Hold the reader instance and inject it into the mutation hook so a
  // lesson-complete write (optimistic for registered, setGuestData for guests)
  // lands on the SAME `learningCenterData` the `module` memo reads — otherwise
  // the writer's `useLearningCenterMutation` spins up an ISOLATED
  // `useLearningCenter` instance and the ModuleProgress stepper stars never
  // update on a "Continue: Lesson" click (stale until reload / root revalidation).
  const lc = useLearningCenter();
  const { learningCenterData, getModuleByID, getLevelByID, getGoalByID } = lc;
  const { completeLesson, updateQuiz, startModule, setActiveLesson } =
    useLearningCenterMutation(lc);
  const { awardLevelPoints } = useAwardLevelPoints();

  // Build the view-model by overlaying SFCC progress onto the Builder entry.
  const module = useMemo<TransformedModule | null>(() => {
    if (!moduleEntry?.id) return null;
    const businessId = (moduleEntry as { data?: { id?: string } }).data?.id;
    const sfccModule = businessId
      ? (getModuleByID(businessId) ?? undefined)
      : undefined;
    return createModule(moduleEntry as RawModuleEntry, sfccModule);
    // getModuleByID is memoized on learningCenterData; list the value, not the fn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleEntry, learningCenterData]);

  const [selectedAccordionItem, setSelectedAccordionItem] = useState<
    string | null
  >(() => {
    if (typeof window !== "undefined") {
      return window.location.hash.slice(1) || null;
    }
    return null;
  });
  const [quizStatus, setQuizStatus] = useState<QuizStatus>(quizStatuses.OPEN);
  const [requestInProgress, setRequestInProgress] = useState(false);

  // Any level marked complete in the learner's progress → show the
  // "See My Points" CTA after a passing quiz (composable parity).
  const isLevelComplete = useMemo(
    () =>
      Boolean(
        learningCenterData?.goals?.some(goal =>
          goal?.levels?.some(level => Boolean(level?.complete)),
        ),
      ),
    [learningCenterData?.goals],
  );

  // Which accordion item opens before the user interacts: a user selection wins;
  // otherwise the quiz (all lessons done) or the first incomplete lesson.
  const activeAccordionItem = useMemo(() => {
    if (selectedAccordionItem !== null) return selectedAccordionItem;
    if (!module) return null;
    return module.allLessonsComplete
      ? (module.quiz?.id ?? null)
      : module.firstIncompleteLessonID;
  }, [module, selectedAccordionItem]);

  // SFCC-11733: pin the initial open item ONCE when the module is first ready.
  // Otherwise `activeAccordionItem` tracks the LIVE `firstIncompleteLessonID`,
  // which advances when the on-mount markOnly seed (SFCC-11729) completes the
  // first lesson — flipping the controlled `<Accordion value>` and flashing the
  // open item on load. A hash deep-link / user selection (non-null
  // `selectedAccordionItem`) wins and is left untouched.
  const didPinInitialItemRef = useRef(false);
  useEffect(() => {
    if (didPinInitialItemRef.current) return;
    if (selectedAccordionItem !== null) {
      didPinInitialItemRef.current = true;
      return;
    }
    if (!module) return;
    didPinInitialItemRef.current = true;
    const initialId = module.allLessonsComplete
      ? (module.quiz?.id ?? null)
      : module.firstIncompleteLessonID;
    if (initialId) setSelectedAccordionItem(initialId);
  }, [module, selectedAccordionItem]);

  // Once the module is complete, the quiz tab always shows PASSED regardless of
  // the in-session `quizStatus` (prevents a stale FAILED after reload).
  const currentQuizStatus = useMemo<QuizStatus>(
    () => (module?.complete ? quizStatuses.PASSED : quizStatus),
    [module?.complete, quizStatus],
  );

  const parentLevelResult = useFetchParentLevel(module?.contentID);
  const parentLevelEntry = parentLevelResult.data as BuilderContent | null;
  const levelFields = parentLevelEntry?.data as LevelFields | undefined;

  const parentGoalResult = useFetchParentGoal(parentLevelEntry?.id);
  const parentGoalEntry = parentGoalResult.data as BuilderContent | null;
  const goalFields = parentGoalEntry?.data as GoalFields | undefined;

  const ctaType = useMemo<CtaType | null>(() => {
    if (!module?.complete) return null;

    const sfccLevel = getLevelByID(levelFields?.id ?? "");
    const levelModules = levelFields?.modules;
    const allModulesComplete = levelModules
      ? areAllModulesComplete(levelModules, sfccLevel?.modules ?? [])
      : false;

    let allLevelsComplete = false;
    if (allModulesComplete && goalFields?.levels) {
      const sfccGoal = getGoalByID(goalFields.id ?? "");
      allLevelsComplete = areAllLevelsComplete(
        goalFields.levels,
        sfccGoal?.levels ?? [],
      );
    }

    if (allLevelsComplete) return ctaTypes.NEXT_GOAL;
    if (allModulesComplete) return ctaTypes.RETURN_TO_GOAL;
    return ctaTypes.NEXT_MODULE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module?.complete, levelFields, goalFields, learningCenterData]);

  const nextModuleId = useMemo(() => {
    if (!levelFields || ctaType !== ctaTypes.NEXT_MODULE) return null;
    const sfccLevel = getLevelByID(levelFields.id ?? "");
    return getNextIncompleteModuleID(
      { modules: levelFields.modules ?? [] },
      sfccLevel ?? undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctaType, levelFields, learningCenterData]);

  const { modulePageUrl: nextModulePageUrl } = useFetchModulePageUrl(
    nextModuleId ?? undefined,
  );

  const nextModuleData = useMemo<NextModuleTarget>(() => {
    switch (ctaType) {
      case ctaTypes.NEXT_MODULE:
        return { id: nextModuleId, url: nextModulePageUrl ?? "" };
      case ctaTypes.RETURN_TO_GOAL:
        return {
          id: null,
          url: goalFields?.dashboard?.value?.data?.url ?? "",
        };
      case ctaTypes.NEXT_GOAL:
        return { id: null, url: "/learning-center" };
      default:
        return { id: null, url: "" };
    }
  }, [ctaType, nextModuleId, nextModulePageUrl, goalFields]);

  // SFCC-11733 (block-level flash): the module player renders off the MODULE
  // entry alone — gate the block's loading/error on the module fetch ONLY, not
  // the chained parent goal/level reverse-fetches. Those feed the breadcrumb/CTA
  // and resolve in the background; including them made the whole block flip
  // skeleton↔content through the module→level→goal fetch chain (and the on-mount
  // guest seed's re-renders) = the visible flashing on load/reload.
  const isError = Boolean(moduleError);
  const componentIsLoading = isModuleLoading;

  const submitLesson = async (
    lesson: ModuleLesson,
    nextLesson: ModuleLesson | undefined,
    opts: { markOnly?: boolean } = {},
  ): Promise<void> => {
    const { markOnly = false } = opts;
    const quizId = module?.quiz?.id;
    // Resume target after this completion: the next lesson, or — on the LAST
    // lesson — the quiz. Passed INTO completeLesson so the lesson-complete and
    // the `activeLessonUrl` move land in ONE write.
    //
    // SFCC-11738: the old last-lesson path completed the lesson, then issued a
    // SEPARATE setActiveLesson write. For registered users that second
    // updateData built its payload from `baseData()` === `learningCenterData`,
    // which is still the STALE pre-optimistic snapshot in the same tick (React
    // hasn't re-rendered, so the closure can't see completeLesson's optimistic
    // overlay). The second write therefore dropped the just-completed last
    // lesson (last-writer-wins) → `allLessonsComplete` never flipped true → the
    // quiz panel stayed blank. Guests were unaffected (their mutations re-read
    // fresh localStorage each call). Folding both into completeLesson's single
    // write removes the clobber.
    const resumeTargetId = nextLesson?.id ?? quizId ?? "";

    if (markOnly) {
      if (lesson.id && module?.id) {
        await completeLesson(lesson.id, nextLesson?.id ?? "", module.id);
      }
      return;
    }

    if (!lesson.complete && lesson.id && module?.id) {
      setRequestInProgress(true);
      // Server-CONFIRMED write: wait for the profile to persist (and the root
      // loader to revalidate) BEFORE advancing — composable parity. The caller
      // (module-accordion) toasts on success / error. `finally` guarantees the
      // button re-enables even if the write throws or revalidate fails.
      try {
        const response = await completeLesson(
          lesson.id,
          resumeTargetId,
          module.id,
          {
            awaitServer: true,
          },
        );
        if (response && !response.success) {
          throw new Error(response.errorMsg);
        }
      } finally {
        setRequestInProgress(false);
      }
    } else if (!nextLesson && quizId) {
      // Last lesson already complete → no completion write to fold into, so just
      // move the resume pointer to the quiz (single write, no clobber race).
      void setActiveLesson(`${window.location.pathname}#${quizId}`);
    }

    setSelectedAccordionItem(
      nextLesson ? (nextLesson.id ?? null) : (quizId ?? null),
    );
  };

  const submitQuiz = async (data: Record<string, string>): Promise<void> => {
    if (!module?.quiz) return;
    setRequestInProgress(true);
    const { id: quizId, questions } = module.quiz;

    // All-or-nothing scoring: first wrong answer fails the whole quiz.
    let quizPassed = true;
    for (let i = 0; i < questions.length; i++) {
      if (
        data[`question-${i}`] !== questions[i].correctAnswerIndex.toString()
      ) {
        quizPassed = false;
        break;
      }
    }

    const result = await updateQuiz(
      quizPassed,
      quizId ?? "",
      module.id,
      // The mutation's `BuilderRef` requires a non-optional id; the Builder
      // entry fields are looser, so cast to the param type (the helpers it calls
      // tolerate missing ids via optional chaining).
      {
        id: levelFields?.id ?? "",
        modules: levelFields?.modules ?? [],
      } as Parameters<typeof updateQuiz>[3],
      {
        id: goalFields?.id ?? "",
        levels: goalFields?.levels ?? [],
      } as Parameters<typeof updateQuiz>[4],
    );
    setRequestInProgress(false);

    // `updateQuiz` returns either an early `{ success:false, errorMsg }` (e.g.
    // module not in profile) or `{ isLevelComplete, isGoalComplete, responseData }`.
    const responseData = (
      "responseData" in result ? result.responseData : result
    ) as UpdatedDataResponse;
    const levelCompleted =
      "isLevelComplete" in result ? result.isLevelComplete : false;

    if (!responseData.success) {
      throw new Error(responseData.errorMsg);
    }
    setQuizStatus(quizPassed ? quizStatuses.PASSED : quizStatuses.FAILED);

    // Level just completed → fire the server-side AnnexCloud award (gating +
    // profile sync happen in the resource action; fire-and-forget).
    if (levelCompleted) {
      awardLevelPoints(levelFields?.loyaltyActionId);
    }
  };

  // The next-step CTA stays disabled until its click has somewhere to go —
  // NEXT_MODULE's page URL arrives via a fetcher that only starts once the next
  // module is known (after the pass). (COM-588.)
  const isNextReady =
    ctaType !== null &&
    Boolean(nextModuleData.url) &&
    (ctaType !== ctaTypes.NEXT_MODULE ||
      Boolean(nextModuleData.id && levelFields?.id));

  /** Resolves `false` (never throws) when it could not navigate. (COM-588.) */
  const goToNextModule = async (target: NextModuleTarget): Promise<boolean> => {
    if (!ctaType || !target.url) return false;
    setRequestInProgress(true);
    try {
      if (ctaType === ctaTypes.NEXT_MODULE) {
        if (!target.id || !levelFields?.id) return false;
        const response = await startModule(
          target.id,
          levelFields.id,
          null,
          target.url,
        );
        if (!response.success) {
          console.warn(
            "[LearningCenterModule] startModule:",
            response.errorMsg,
          );
          return false;
        }
      } else {
        void setActiveLesson(target.url);
      }
      // Awaited so the CTA stays disabled until the next page has loaded.
      await navigate(target.url);
      return true;
    } catch (error) {
      console.error("[LearningCenterModule] next-step failed:", error);
      return false;
    } finally {
      setRequestInProgress(false);
    }
  };

  return {
    module,
    requestInProgress,
    componentIsLoading,
    isError,
    refetchModule,
    activeAccordionItem,
    setSelectedAccordionItem,
    quizStatus: currentQuizStatus,
    setQuizStatus,
    ctaType,
    nextModuleData,
    isGuest,
    isLevelComplete,
    submitLesson,
    submitQuiz,
    goToNextModule,
    isNextReady,
    setActiveLesson,
  };
}
